import { Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../../components/layout/page';
import { PhysicianDto, UpdatePhysicianDto, api } from '../../../lib/api';
import { queryClient } from '../../../providers/query-client';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { Tag } from '@/components/ui/tag';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import States from '@/components/states';
import { Button } from '@/components/ui/button';
import { LinkButton } from '@/components/ui/link-button';
import { Divider } from '@/components/ui/Divider';
import { Card } from '@/components/ui/card';

export function PhysicianForm() {
    const navigate = useNavigate();
    const params = useParams<'physicianId'>();

    const rhf = useForm<UpdatePhysicianDto>({
        defaultValues: {
            addresses: [
                {
                    address: {
                        address: '',
                        city: '',
                        state: '',
                        zipcode: '',
                        type: '',
                    },
                    phone: '',
                    fax: '',
                },
            ],
        },
        reValidateMode: 'onBlur',
    });

    const { fields } = useFieldArray({
        control: rhf.control,
        name: 'addresses',
    });

    // clean up on unmount
    useEffect(() => {
        return () => queryClient.removeQueries({ queryKey: ['physician', params.physicianId] });
    }, []);

    // fetch data
    const { isFetched } = useQuery<PhysicianDto | void>({
        queryKey: ['physician', params.physicianId],
        queryFn: async () => {
            try {
                const { data, status } = await api.physician.get(params.physicianId as string);

                if (status !== 200 || !data) {
                    navigate('/settings/physicians');
                }

                rhf.reset(data);

                return data;
            } catch {
                navigate('/settings/physicians');
            }
        },
        enabled: Boolean(params.physicianId),
    });

    // update data
    const { mutate } = useMutation<unknown, unknown, UpdatePhysicianDto>({
        mutationFn: async (data) => {
            if (!params.physicianId) {
                return;
            }

            const response = await api.physician.edit(params?.physicianId, data);

            return response;
        },
        onSuccess: () => {
            navigate('/settings/physicians');
        },
    });

    if (!isFetched) {
        return (
            <Spinner
                borderWidth="4px"
                animationDuration="0.65s"
                css={{ '--spinner-track-color': 'purple.100' }}
                color="purple.500"
                size="lg"
            />
        );
    }

    return (
        <Page>
            <FormProvider {...rhf}>
                <form noValidate onSubmit={rhf.handleSubmit((values) => mutate(values))}>
                    <Flex gap="8" flexDirection="column" width="100%" css={{ '--field-label-width': 0 }}>
                        <Card>
                            <FieldGroup>
                                <Field orientation="horizontal" label="NPI:" justifyContent="start">
                                    <Text>{rhf.getValues('npi')}</Text>
                                </Field>

                                <Field orientation="horizontal" label="Credentials:" justifyContent="start">
                                    <Text>{rhf.getValues('credential')}</Text>
                                </Field>

                                <Field orientation="horizontal" label="PECOS:" justifyContent="start">
                                    <Tag
                                        colorPalette={rhf.getValues('pecos') ? 'green' : 'red'}
                                        size="sm"
                                        textTransform="uppercase"
                                        fontWeight="700">
                                        {rhf.getValues('pecos') ? 'enrolled' : 'not enrolled'}
                                    </Tag>
                                </Field>
                            </FieldGroup>
                        </Card>
                        {fields?.map((_, index) => {
                            return (
                                <Card>
                                    <FieldGroup>
                                        <Field label="Address">
                                            <Input type="text" {...rhf.register(`addresses.${index}.address.address`)} />
                                        </Field>

                                        <FieldRow>
                                            <Field label="City">
                                                <Input type="text" {...rhf.register(`addresses.${index}.address.city`)} />
                                            </Field>

                                            <Field label="State">
                                                <NativeSelectRoot {...rhf.register(`addresses.${index}.address.state`)}>
                                                    <NativeSelectField>
                                                        <States />
                                                    </NativeSelectField>
                                                </NativeSelectRoot>
                                            </Field>

                                            <Field label="Zipcode">
                                                <Input type="text" {...rhf.register(`addresses.${index}.address.zipcode`)} />
                                            </Field>
                                        </FieldRow>
                                        <Divider colorPalette="gray" />

                                        <FieldRow>
                                            <Field label="Phone">
                                                <Input type="text" {...rhf.register(`addresses.${index}.phone`)} maxLength={10} />
                                            </Field>

                                            <Field label="Fax">
                                                <Input type="text" {...rhf.register(`addresses.${index}.fax`)} maxLength={10} />
                                            </Field>
                                        </FieldRow>
                                    </FieldGroup>
                                </Card>
                            );
                        })}

                        <Flex alignItems="flex-start" gap="4">
                            <Button type="submit">Save</Button>
                            <LinkButton to="/settings/physicians">Cancel</LinkButton>
                        </Flex>
                    </Flex>
                </form>
            </FormProvider>
        </Page>
    );
}
